import moment from 'moment';
import {
  LOAD_ACTIVITY_LIST,
  SET_ACTIVITY_LIST,
  CREATE_ACTIVITY,
  DELETE_ACTIVITY,
  RESET_ACTIVITY,
} from './actionTypes';
import {
  getFirestore,
  collection,
  doc,
  getDocs,
  addDoc,
  deleteDoc,
  query,
  orderBy
} from 'firebase/firestore';
import { api } from '../util';
import urls from '../constants/urls';
import { ACTIVITY_COLLECTION, ACTIVITY_SUB_COLLECTION } from '../firebase/collections';
import { caseConstants } from '../constants';
import { companyRoles } from '../constants/userRoles';

// Initialize Firestore
const db = getFirestore();
const { DEFAULT_COMPANY_ID } = caseConstants;

const getActivityTimestamp = (value) => {
  if (!value) {
    return 0;
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate().getTime();
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === 'number') {
    return value;
  }

  const timestamp = moment(value).valueOf();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

export const setActivityList = (activityList) => ({ type: SET_ACTIVITY_LIST, activityList });

const getActivityCompany = (activity = {}, state = {}) => {
  if (activity.company) {
    return activity.company;
  }

  if (!activity.caseId) {
    return null;
  }

  const cases = [
    state.cases?.activeCase,
    ...(state.cases?.list || []),
    ...(state.cases?.completedCases || []),
    ...(state.cases?.archivedCases || []),
  ].filter(Boolean);

  const relatedCase = cases.find((item) => (
    item.id === activity.caseId ||
    item.formattedId === activity.caseId
  ));

  return relatedCase?.company || null;
};

const withActivityCompany = (activity = {}, state = {}) => {
  const company = getActivityCompany(activity, state);

  return company ? { ...activity, company } : activity;
};

const isActivityVisibleToUser = (activity = {}, currentUser = {}) => {
  if (!companyRoles.includes(currentUser?.role)) {
    return true;
  }

  return (activity.company || DEFAULT_COMPANY_ID) === (currentUser.company || DEFAULT_COMPANY_ID);
};

export const fetchActivity = () => (dispatch, getState) => {
  const state = getState();
  const currentUser = state.user.currentUser;
  const uid = currentUser.uid;
  const activityCollection = collection(db, ACTIVITY_COLLECTION, uid, ACTIVITY_SUB_COLLECTION);

  const date = moment().subtract(14, 'days');

  // Query the sub-collection, ordering by date in descending order
  const activityQuery = query(activityCollection, orderBy('date', 'desc'));

  return getDocs(activityQuery)
    .then((snapshot) => snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
    .then((activity) =>
      dispatch(setActivityList(
        activity
          ?.map((item) => withActivityCompany(item, state))
          ?.filter((item) => isActivityVisibleToUser(item, currentUser))
          ?.filter((item) => moment(item.date).isAfter(date))
          ?.sort((a, b) => getActivityTimestamp(b.date) - getActivityTimestamp(a.date))
      ))
    );
};

export const createActivity = (uid, activity) => (dispatch, getState) => {
  const state = getState();
  const notificationsSettings = state.settings?.notifications;
  const activityCollection = collection(db, ACTIVITY_COLLECTION, uid, ACTIVITY_SUB_COLLECTION);
  const nextActivity = withActivityCompany(activity, state);
  const recipient = state.users?.list?.find((user) => user.uid === uid);

  if (recipient && !isActivityVisibleToUser(nextActivity, recipient)) {
    return Promise.resolve(null);
  }

  dispatch({ type: LOAD_ACTIVITY_LIST });

  return addDoc(activityCollection, nextActivity)
    .then(() => {
      dispatch({ type: CREATE_ACTIVITY, activity: nextActivity });
      if (notificationsSettings.active) {
        createPushNotification(uid, { title: nextActivity?.title, body: nextActivity?.text }).catch();
      }
      return dispatch(fetchActivity());
    })
    .catch((error) => console.log('Error creating activity:', error));
};

export const createPushNotification = (uid, notification) => {
  return api.post(urls.sendPushNotification, {
    uid,
    title: notification?.title || '',
    body: notification?.body || ''
  });
};

export const deleteActivity = (id, uid) => (dispatch, getState) => {
  const state = getState();
  const userId = uid || state.user.currentUser.uid;
  const activityDoc = doc(db, ACTIVITY_COLLECTION, userId, ACTIVITY_SUB_COLLECTION, id);

  dispatch({ type: LOAD_ACTIVITY_LIST });

  return deleteDoc(activityDoc)
    .then(() => dispatch({ type: DELETE_ACTIVITY, id }))
    .catch((error) => console.log('Error deleting activity:', error));
};

export const resetActivity = () => ({ type: RESET_ACTIVITY });
